import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { AD_HOC_OPTION_VALUE, ContactAgreementState, classifyContactAgreementState } from '~/components/BillModal/GlobalBillModal/GlobalBillModal.utils';
import { useContactAgreements } from '~/components/BillModal/GlobalBillModal/hooks/useContactAgreements';
import { useStartAdHocBill } from '~/components/BillModal/GlobalBillModal/hooks/useStartAdHocBill';
import { useBillModalLauncher } from '~/components/BillModal/hooks/useBillModalLauncher';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
export const useGlobalBillModal = (param)=>{
    let { dismissModal } = param;
    const { t: globalBillModalTexts } = useLocaleKeys('components.globalBillModal');
    const { AD_HOC_INVOICING } = useFeatureFlags();
    const { openBillClient } = useBillModalLauncher();
    const { startAdHocBill, isCreatingAdHoc } = useStartAdHocBill();
    const { control, watch, setValue, handleSubmit } = useForm({
        mode: 'onSubmit'
    });
    const [selectedContact, selectedAgreement] = watch([
        'selectedContactId',
        'selectedAgreement'
    ]);
    const { agreements, adHocSummary, isLoading: isAgreementsLoading } = useContactAgreements(selectedContact !== null && selectedContact !== void 0 ? selectedContact : '');
    const contactAgreementState = AD_HOC_INVOICING ? classifyContactAgreementState(agreements, adHocSummary) : ContactAgreementState.OnlySignedAgreements;
    const isAdHocSelected = (selectedAgreement === null || selectedAgreement === void 0 ? void 0 : selectedAgreement.value) === AD_HOC_OPTION_VALUE;
    const hasAdHocOption = contactAgreementState === ContactAgreementState.SignedAndAdHoc;
    const adHocOption = useMemo(()=>({
            label: globalBillModalTexts.invoicesWithoutAgreement(),
            value: AD_HOC_OPTION_VALUE
        }), [
        globalBillModalTexts
    ]);
    const agreementOptions = useMemo(()=>hasAdHocOption ? [
            ...agreements,
            adHocOption
        ] : agreements, [
        agreements,
        hasAdHocOption,
        adHocOption
    ]);
    useEffect(()=>{
        if (agreements.length === 1 && adHocSummary == null) {
            setValue('selectedAgreement', agreements[0]);
        }
    }, [
        agreements,
        adHocSummary,
        setValue
    ]);
    const handleChangeContact = ()=>{
        setValue('selectedAgreement', null);
    };
    const openAdHocBilling = ()=>{
        const hasExistingAdHoc = contactAgreementState === ContactAgreementState.SignedAndAdHoc && isAdHocSelected && adHocSummary != null;
        startAdHocBill({
            contactId: selectedContact,
            existingAdHocRelationshipId: hasExistingAdHoc ? adHocSummary.id : undefined,
            onOpened: dismissModal
        });
    };
    const onContinue = ()=>{
        if (isAdHocSelected || contactAgreementState === ContactAgreementState.NoSignedAgreement) {
            openAdHocBilling();
            return;
        }
        if (!(selectedAgreement === null || selectedAgreement === void 0 ? void 0 : selectedAgreement.value)) return;
        openBillClient(selectedAgreement.value);
        dismissModal();
    };
    const continueRoutesDirectly = isAdHocSelected || contactAgreementState === ContactAgreementState.NoSignedAgreement;
    const primaryButtonClick = continueRoutesDirectly ? onContinue : handleSubmit(onContinue);
    return {
        control,
        selectedContact,
        selectedAgreement,
        contactAgreementState,
        agreementOptions,
        hasAdHocOption,
        showAgreementSelector: agreementOptions.length > 1,
        isPrimaryButtonLoading: isCreatingAdHoc || isAgreementsLoading,
        isAgreementsLoading,
        handleChangeContact,
        primaryButtonClick
    };
};
